import type { Milestone, Phase, PhaseKind } from './executiveSummaryTypes';

import { SEARCE_COLORS } from '../../theme';

const COMPLETED_STATUSES = new Set(['sign off', 'completed']);

const OVERDUE_TINT_STATUSES = new Set(['yet to start', 'on hold', 'in progress']);

const RING_LABEL_MAX_LEN = 20;

/** Build ring timeline nodes from table milestones so counts always match. */
export function phasesFromMilestones(milestones: Milestone[]): { phases: Phase[]; fillTo: number } {
  if (milestones.length === 0) {
    return { phases: [{ label: 'MILESTONES', kind: 'outline' }], fillTo: 0 };
  }
  let seenNonComplete = false;
  const phases: Phase[] = milestones.map((m, i) => {
    const name = (m.milestone || '').trim() || `Stage ${i + 1}`;
    const label = name.slice(0, RING_LABEL_MAX_LEN).toUpperCase();
    let kind: PhaseKind;
    if (isMilestoneCompleted(m.status)) {
      kind = 'done';
    } else if (!seenNonComplete) {
      kind = 'curr';
      seenNonComplete = true;
    } else {
      kind = 'pend';
    }
    return { label, kind };
  });
  const fillTo = phases.filter((p) => p.kind === 'done').length;
  return { phases, fillTo };
}

/** Canonical display order for milestone status filters. */
export const MILESTONE_STATUS_ORDER = [
  'Cancelled',
  'Sign Off',
  'Yet to Start',
  'Completed',
  'In Progress',
  'On Hold',
] as const;

export function isMilestoneCompleted(status: string): boolean {
  return COMPLETED_STATUSES.has(status.trim().toLowerCase());
}

function parsePlannedDate(plannedDate: string): Date | null {
  if (!plannedDate || plannedDate === '—') return null;
  const d = new Date(plannedDate);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Red row tint when active milestones are past their planned date. */
export function isMilestoneOverdue(status: string, plannedDate: string): boolean {
  if (!OVERDUE_TINT_STATUSES.has(status.trim().toLowerCase())) return false;
  const planned = parsePlannedDate(plannedDate);
  if (!planned) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  planned.setHours(0, 0, 0, 0);
  return today > planned;
}

export function getMilestoneRingNodeStyle(
  status: string,
  plannedDate: string,
): { bg: string; border: string; color: string; labelColor: string } {
  if (isMilestoneOverdue(status, plannedDate)) {
    return {
      bg: SEARCE_COLORS.error,
      border: '#b91c1c',
      color: '#fff',
      labelColor: '#b91c1c',
    };
  }

  const key = status.trim().toLowerCase();
  switch (key) {
    case 'completed':
    case 'sign off':
      return {
        bg: SEARCE_COLORS.success,
        border: '#15803d',
        color: '#fff',
        labelColor: SEARCE_COLORS.success,
      };
    case 'in progress':
      return {
        bg: '#1d4ed8',
        border: '#1e40af',
        color: '#fff',
        labelColor: '#1d4ed8',
      };
    case 'yet to start':
      return {
        bg: '#b45309',
        border: '#92400e',
        color: '#fff',
        labelColor: '#b45309',
      };
    case 'on hold':
      return {
        bg: '#64748b',
        border: '#475569',
        color: '#fff',
        labelColor: '#64748b',
      };
    case 'cancelled':
      return {
        bg: '#9ca3af',
        border: '#6b7280',
        color: '#fff',
        labelColor: '#6b7280',
      };
    default:
      return {
        bg: '#e2e8f0',
        border: '#94a3b8',
        color: SEARCE_COLORS.textSecondary,
        labelColor: SEARCE_COLORS.neutral_500,
      };
  }
}

export function getMilestoneStatusPill(status: string): {
  bg: string;
  color: string;
  label: string;
  weight: number;
} {
  const key = status.trim().toLowerCase();
  switch (key) {
    case 'completed':
    case 'sign off':
      return { bg: '#ecfdf5', color: SEARCE_COLORS.success, label: status, weight: 700 };
    case 'in progress':
      return { bg: '#eff6ff', color: '#1d4ed8', label: status, weight: 700 };
    case 'yet to start':
      return { bg: '#fffbeb', color: '#b45309', label: status, weight: 700 };
    case 'on hold':
      return { bg: '#f1f5f9', color: '#64748b', label: status, weight: 700 };
    case 'cancelled':
      return { bg: '#f3f4f6', color: '#6b7280', label: status, weight: 700 };
    default:
      return {
        bg: '#f8fafc',
        color: SEARCE_COLORS.textSecondary,
        label: status.trim() || '—',
        weight: 600,
      };
  }
}

export function milestoneStatusFilterOptions(milestones: { status: string }[]): { id: string; label: string }[] {
  const present = new Set(milestones.map((m) => m.status.trim()).filter(Boolean));
  const ordered = MILESTONE_STATUS_ORDER.filter((s) => present.has(s));
  const extras = Array.from(present).filter(
    (s) => !MILESTONE_STATUS_ORDER.includes(s as (typeof MILESTONE_STATUS_ORDER)[number]),
  );
  return [{ id: 'all', label: 'All' }, ...ordered.map((s) => ({ id: s, label: s })), ...extras.map((s) => ({ id: s, label: s }))];
}
